// Angular
import { inject, Injectable, OnDestroy } from '@angular/core';
import { Subject, Subscription } from 'rxjs';

// Data
import { FieldMetadata, FieldMetadataUtil, UiCommandRenderCell } from '@data/cer-data-types';
import { CerDataService } from '@data/cer-data.service';

// Form
import { CerFormNavigationService } from '@form/cer-form-navigation.service';

// UI
import { CerEventSubAction, UiActionTypeEnum, UiCommand, UiCommandEvent, UiCommandSourceEnum, UiKeyboardShortcut } from '@ui/cer-ui-types';
import { uiCmdClipboardCopyField, uiCmdClipboardCopyRow, uiCmdClipboardCopyRowWithHeader, uiCmdColumnFilter, uiCmdColumnFilterClear, uiCmdColumnHide, uiCmdColumnGroup, uiCmdColumnNavigateFK, uiCmdColumnNavigateFKRow, uiCmdColumnsChoose, uiCmdComponentMaximize, uiCmdDebugModeToogle, uiCmdEditModeTooggle, uiCmdEditStart, uiCmdExportExcel, uiCmdImportExcel, uiCmdFilterByClipboard, uiCmdFilterCellValue, uiCmdFilterClear, uiCmdFilterQuery, uiCmdFilterShow, uiCmdResearch, uiCmdSearchBarFocus, uiCmdSearchClear, uiCommandPersistenceComplete, uiCommandPersistenceStandard, uiCmdFilterCellValueNot, uiCmdNavigateItem, uiCmdNavigate } from '@ui/cer-ui-commands';

// SyncFusion
import { Tooltip } from '@syncfusion/ej2-angular-popups';
import { BatchAddArgs, BatchDeleteArgs, BeforeBatchAddArgs, BeforeBatchDeleteArgs, BeforeBatchSaveArgs, CellSelectEventArgs, ContextMenuClickEventArgs, ContextMenuItemModel, EditEventArgs, getForeignKeyData, GridComponent, parentsUntil, QueryCellInfoEventArgs, Render, ToolbarItem, ToolbarItems } from '@syncfusion/ej2-angular-grids';
import { DataManager, Query } from '@syncfusion/ej2-data';
import { DropDownButton } from '@syncfusion/ej2-splitbuttons';
import { ItemModel, MenuItem } from '@syncfusion/ej2-angular-navigations';

// Grid
import { CerGridComponent, CerGridToolbarType } from './cer-grid.component';
import { GridDesign } from './cer-grid-design';
import { CerDataMetaService } from '@data/cer-data-meta.service';
import { enumFromString } from '../cer-util/cer-util';
import { CerFormContextService } from '@form/cer-form-context.service';
import { CerFormView } from '@form/cer-form-view';
import { CerToolbarCommandAdapter, toolbarItemTitle } from '@cer/cer-toolbar/cer-toolbar-command-adapter';

@Injectable()
export class CerGridCommandService implements OnDestroy {
  // Context menu
  public gridContextMenuSeparatorHeader: ContextMenuItemModel = { separator: true, target: ".e-headercontent" };
  public gridContextMenuSeparatorContent: ContextMenuItemModel = { separator: true, target: ".e-content" };
  private gridContextMenuItems: ContextMenuItemModel[] = [];

  private gridContextMenuItemsHeader: ContextMenuItemModel[] = [
    this.contextMenuItemCreate(uiCmdColumnsChoose),
    this.contextMenuItemCreate(uiCmdColumnHide),
    this.gridContextMenuSeparatorHeader,
    this.contextMenuItemCreate(uiCmdColumnFilter),
    this.contextMenuItemCreate(uiCmdColumnFilterClear),
    this.contextMenuItemCreate(uiCmdColumnGroup),
    this.gridContextMenuSeparatorHeader,
    this.contextMenuItemCreate(uiCmdFilterClear),
    this.contextMenuItemCreate(uiCmdResearch),
    this.gridContextMenuSeparatorHeader,
    this.contextMenuItemCreate(uiCmdExportExcel),
    this.contextMenuItemCreate(uiCmdImportExcel),
    this.gridContextMenuSeparatorHeader,
    this.contextMenuItemCreate(uiCmdColumnNavigateFK)
  ];
  private gridContextMenuItemsContentFiltering: ContextMenuItemModel[] = [
    this.contextMenuItemCreate(uiCmdFilterCellValue),
    this.contextMenuItemCreate(uiCmdFilterCellValueNot),
    this.contextMenuItemCreate(uiCmdFilterByClipboard),
    this.gridContextMenuSeparatorContent
  ];

  private gridContextMenuItemsContent: ContextMenuItemModel[] = [
    this.contextMenuItemCreate(uiCmdClipboardCopyField),
    this.contextMenuItemCreate(uiCmdClipboardCopyRow),
    this.contextMenuItemCreate(uiCmdClipboardCopyRowWithHeader),
    //this.contextMenuItemCreate(uiCmdSelectionModeSingle),
    //this.contextMenuItemCreate(uiCmdSelectionModeMulti),
    this.gridContextMenuSeparatorContent,
    this.contextMenuItemCreate(uiCmdColumnNavigateFKRow)
  ];

  private gridContextMenuItemsEdit: ContextMenuItemModel[] =
    [this.contextMenuItemCreate(uiCmdEditStart),
    this.gridContextMenuSeparatorContent];

  // Keyboard event
  public keyboardShortcuts: UiKeyboardShortcut[] =
    [
      { cmd: uiCmdExportExcel, code: 'KeyE', alt: false, ctrl: true, shift: true, enabled: true },
      { cmd: uiCmdImportExcel, code: 'KeyI', alt: false, ctrl: true, shift: true, enabled: true },
      { cmd: uiCmdColumnsChoose, code: 'KeyK', alt: false, ctrl: true, shift: true, enabled: true },
      { cmd: uiCmdResearch, code: 'F5', alt: true, ctrl: true, shift: false, enabled: true },
      //{ cmd: uiCmdEditStart, code: 'Space', alt: false, ctrl: false, shift: false, enabled: true },
      //{ cmd: uiCmdPagingToggle, code: 'KeyT', alt: true, ctrl: true, shift: false, enabled: true },
      { cmd: uiCmdSearchBarFocus, code: 'KeyS', alt: true, ctrl: false, shift: false, enabled: true },
      { cmd: uiCmdSearchClear, code: 'KeyS', alt: true, ctrl: true, shift: false, enabled: true },
      { cmd: uiCmdFilterClear, code: 'KeyL', alt: true, ctrl: true, shift: false, enabled: true },
      { cmd: uiCmdClipboardCopyField, code: 'KeyC', alt: false, ctrl: true, shift: false, enabled: true, informationOnly: true },
      { cmd: uiCmdClipboardCopyRowWithHeader, code: 'KeyH', alt: true, ctrl: true, shift: true, enabled: true, informationOnly: true },
      { cmd: uiCmdClipboardCopyRow, code: 'KeyC', alt: true, ctrl: true, shift: false, enabled: true },
      //{ cmd: uiCmdSelectionModeToggle, code: 'KeyR', alt: false, ctrl: true, shift: false, enabled: true },
      { cmd: uiCmdEditModeTooggle, code: 'KeyE', alt: true, ctrl: true, shift: true, enabled: true },
      { cmd: uiCmdDebugModeToogle, code: 'KeyD', alt: true, ctrl: true, shift: true, enabled: true },
      { cmd: uiCmdFilterShow, code: 'KeyQ', alt: true, ctrl: true, shift: false, enabled: true },
      { cmd: uiCmdFilterQuery, code: 'KeyQ', alt: true, ctrl: false, shift: true, enabled: true },
      { cmd: uiCommandPersistenceComplete, code: 'KeyR', alt: true, ctrl: true, shift: false, enabled: true },
      { cmd: uiCommandPersistenceStandard, code: 'KeyT', alt: true, ctrl: true, shift: false, enabled: true }
    ];
  private shortcutBeginEdit: UiKeyboardShortcut = { cmd: uiCmdEditStart, code: 'F2', alt: false, ctrl: false, shift: false, enabled: true };

  // Subscriptions
  private subscriptionManager: Subscription = new Subscription();
  public command$: Subject<UiCommandEvent> = new Subject<UiCommandEvent>();
  public commandPre$: Subject<UiCommandEvent> = new Subject<UiCommandEvent>();  // Emits before command
  public commandPost$: Subject<UiCommandEvent> = new Subject<UiCommandEvent>(); // Emits after command

  // Grid edit
  private appGrid: CerGridComponent;
  private grid: GridComponent;
  private design: GridDesign;

  // Constructor
  constructor(
    private meta: CerDataMetaService,
    private data: CerDataService,
    private formNavigationService: CerFormNavigationService) {
  }

  // Subscriptions
  ngOnDestroy(): void {
    this.touchDestroy();
    this.navigationDownMenuDestroy();
    this.subscriptionManager.unsubscribe();
    this.gridOff();
  }

  private manage(s: Subscription) {
    this.subscriptionManager.add(s);
  }

  public init(appGrid: CerGridComponent, design: GridDesign, toolbarCommands: UiCommand[], contextMenuCommands: UiCommand[], keyboardShortcuts: UiKeyboardShortcut[]) {
    this.appGrid = appGrid;
    this.grid = appGrid.grid;
    this.design = design;
    this.gridInit();
    this.touchInit();
    this.toolbarInit();
    this.toolbarCommandsAdd(toolbarCommands);
    this.contextMenuCommandsInit(contextMenuCommands);
    this.keyboardShortcutsAdd(keyboardShortcuts);
  }

  // State
  public dataSourceSet(dm: any, query: any = null, reverse: boolean = false) {
    if (this.grid?.renderModule) {
      var module = this.grid?.renderModule ? this.grid?.getDataModule() : null;
      if (module && module.dataManager?.dataSource.json == null) {
        module.isQueryInvokedFromData = true; // Structure changing -> Execute query is called anyway
      }
      if (query) {
        this.grid.query = query;
      }
      if (this.grid.dataSource) {
        if (reverse) {
          this.grid.renderModule.data.dataManager = dm;
          this.grid.dataSource = dm;
        }
        else {
          //var state : PendingState = {isPending: true, resolver: this.stateResolver};
          //this.grid.renderModule.data.setState(state);
          this.grid.dataSource = dm;
          this.grid.renderModule.data.dataManager = dm;
        }
      }
    }
  }

  public dataSourceQueryClone() {
    //var module: Render = this.grid?.renderModule;
    //var ds: any = module?.data?.dataManager?.dataSource;
    if (this.grid.query) {
      this.grid.query = this.grid.query.clone();
    }

  }

  public dataSetOffline(offline: boolean) {
    var ds: any = this.data?.dataManager?.dataSource;
    if (ds != null) {
      if ((offline == true) || (ds.offline == true)) {
        ds.offline = offline;
      }
    }
  }

  public dataSourceSetOffline(offline: boolean) {
    var module: Render = this.grid?.renderModule;
    if (module?.data?.dataManager?.dataSource) {
      module.data.dataManager.dataSource.offline = offline;
    }
    else if ((<any>this.grid?.dataSource)?.dataSource) {
      (<any>this.grid.dataSource).dataSource.offline = offline;
    }
  }

  public dataSourceInit() {
    this.dataSetOffline(false);
    this.dataSourceSet(this.data?.dataManager, this.data?.query);
  }

  public dataSourceInitLocalEmpty() {
    var dm: DataManager = this.data?.dataSourceLocalGet([]);
    var query = new Query();
    this.dataSourceSet(dm, query);
  }

  public dataSourceInitLocal(rows : any[] = null) {
    this.dataSourceSet(this.data?.dataSourceLocalGet(
      rows ?? this.grid?.getDataRows()));
    if (this.grid) {
      (<any>this.grid).localDataTempActive = true;
    }
  }

  // Disable grid
  public isDisabled(): boolean {
    return !this.isEnabled();
  }

  public isEnabled(): boolean {
    var enabled: boolean = true;
    if (this.grid && this.grid.element) {
      var cls: DOMTokenList = this.grid.element.classList;
      var clsName = 'app-disable';
      if (cls.contains(clsName)) {
        enabled = false;
      }
    }
    return enabled;
  }

  public disable() {
    this.enable(false);
  }

  public enable(enable: boolean = true) {
    if (this.grid && this.grid.element) {
      var cls: DOMTokenList = this.grid.element.classList;
      var clsName = 'app-disable';
      if (enable) {
        if (cls.contains(clsName)) {
          cls.remove(clsName);
          //document.getElementById('GridParent').classList.remove('wrapper');
        }
      }
      else {
        if (!cls.contains(clsName)) {
          this.grid.element.classList.add(clsName);
          //document.getElementById('GridParent').classList.add('wrapper');
        }
      }
    }
  }

  // Command dispatcher
  public cmdDispatchActionBegin(actionType: UiActionTypeEnum, args: any = null) {
    this.cmdDispatch(UiCommandSourceEnum.ActionBegin, null, args, null, actionType);
  }

  public cmdDispatchActionComplete(actionType: UiActionTypeEnum, args: any = null) {
    this.cmdDispatch(UiCommandSourceEnum.ActionComplete, null, args, null, actionType);
  }

  public cmdDispatchCommand(actionType: UiActionTypeEnum, args: any = null) {
    this.cmdDispatch(UiCommandSourceEnum.Command, args, null, null, actionType);
  }

  public cmdDispatch(source: UiCommandSourceEnum, commandId: string, args: any, columnName: string = null, actionType: UiActionTypeEnum = null, rowsData: any[] = null, rowData: any = null,
    command: UiCommand = null, subAction: CerEventSubAction = 'none', rowDataOriginal: any = null): boolean {
    var event: UiCommandEvent = {
      appComponent: this.appGrid,
      formView: this.appGrid?.formView,
      viewMetadata: this.appGrid?.viewMetadata,
      source: source,
      commandId: commandId,
      args: args,
      cancel: false,
      columnName: columnName,
      actionType: actionType,
      subAction: subAction,
      rowsData: rowsData,
      rowData: rowData,
      rowDataOriginal: rowDataOriginal,
      command: command
    };

    if (this.isDisabled()) {
      return true;
    }

    this.commandPre$.next(event);

    if (!event.cancel) {
      this.cmdDispatchInternal(event);
    }

    if (!event.cancel) {
      this.command$.next(event);
    }

    if (!event.cancel) {
      this.commandPost$.next(event);
    }

    if (event.cancel === true && event.args) {
      event.args.cancel = true;
    }

    return event.cancel;
  }

  private cmdDispatchInternal(event: UiCommandEvent) {
    switch (event.source) {
      case UiCommandSourceEnum.DesignChange:
        this.toolbarReflow();
        break;
      case UiCommandSourceEnum.Command:
        switch (event.actionType) {
          case UiActionTypeEnum.Reflow:
            this.toolbarReflow();
            break;
        }
        break;
      case UiCommandSourceEnum.ActionComplete:
        switch (event.actionType) {
          case UiActionTypeEnum.Created:
            this.toolbarInitComplete();
            break;
          case UiActionTypeEnum.Loaded:
            this.keyboardShortcutsDefaultModify();
            break;
          case UiActionTypeEnum.DataBound:
            this.navigationDropDownMenuRender(event);
            this.commandsEnableBySelection(false);
            break;
          case UiActionTypeEnum.Refresh:
            this.toolbarTitleResetCount();
            break;
          case UiActionTypeEnum.RowSelect:
            this.commandsEnableBySelection(true);
            break;
          case UiActionTypeEnum.RowDeselect:
            this.commandsEnableBySelection(false);
            break;
        }
        break;
      case UiCommandSourceEnum.ContextMenu:
        switch (event.actionType) {
          case UiActionTypeEnum.ContextMenuOpen:
            this.onContextMenuOpenNavigate(event);
            break;
        }
        break;
    }
    switch (event.commandId) {
      case uiCmdColumnNavigateFK.id:
      case uiCmdColumnNavigateFKRow.id:
        this.doColumnNavigateFK(event);
        break;
    }
  }

  private isGridReady(): boolean {
    return (this.grid?.headerModule != undefined);
  }

  public reflow() {
    if (this.grid && !this.grid.isEdit) {
      this.cmdDispatchCommand(UiActionTypeEnum.Reflow);
    }
  }

  public refresh() {
    if (this.isGridReady()) {
      this.appGrid.rowSelectedKeep();
      this.grid.refresh();
    }
  }

  public refreshColumns() {
    if (this.isGridReady()) {
      this.appGrid.rowSelectedKeep();
      this.grid.refreshColumns();
    }
  }

  /*******************
   * Grid Events
   ******************/

  private gridInit() {
    var g: GridComponent = this.grid;
    this.manage(g.load.subscribe((e: any) => this.gridOnLoad(e)));
    this.manage(g.created.subscribe((e: any) => this.gridOnCreated(e)));
    this.manage(g.destroyed.subscribe((e: any) => this.gridOnDestroyed(e)));
    this.manage(g.dataStateChange.subscribe((e: any) => this.gridOnDataStateChange(e)));
    this.manage(g.beforeDataBound.subscribe((e: any) => this.gridOnDataBoundBegin(e)));
    this.manage(g.dataBound.subscribe((e: any) => this.gridOnDataBoundComplete(e)));
    this.manage(g.dataSourceChanged.subscribe((e: any) => this.gridOndataSourceChangedComplete(e)));
    this.manage(g.actionFailure.subscribe((e: any) => this.gridOnActionFailure(e)));
    this.manage(g.actionBegin.subscribe((e: any) => this.gridOnActionBegin(e)));
    this.manage(g.actionComplete.subscribe((e: any) => this.gridOnActionComplete(e)));
    this.manage(g.cellSelecting.subscribe((e: any) => this.gridOnCellSelecting(e)));
    this.manage(g.batchAdd.subscribe((e: BatchAddArgs) => this.gridOnAfterBatchAdd(e)));
    this.manage(g.batchDelete.subscribe((e: BatchDeleteArgs) => this.gridOnAfterBatchDelete(e)));
    this.manage(g.batchCancel.subscribe((e: any) => this.gridOnAfterBatchCancel(e)));
    this.manage(g.beforeBatchAdd.subscribe((e: BeforeBatchAddArgs) => this.gridOnBeforeBatchAdd(e)));
    this.manage(g.beforeBatchDelete.subscribe((e: BeforeBatchDeleteArgs) => this.gridOnBeforeBatchDelete(e)));
    this.manage(g.beforeBatchSave.subscribe((e: BeforeBatchSaveArgs) => this.gridOnBeforeBatchSave(e)));
    this.manage(g.cellSelected.subscribe((e: CellSelectEventArgs) => this.gridOnCellSelected(e)));
    this.manage(g.cellDeselected.subscribe((e: any) => this.gridOnCellDeselected(e)));
    this.manage(g.rowSelecting.subscribe((e: any) => this.gridOnRowSelecting(e)));
    this.manage(g.rowSelected.subscribe((e: any) => this.gridOnRowSelected(e)));
    this.manage(g.rowDeselected.subscribe((e: any) => this.gridOnRowDeselected(e)));
    this.manage(g.commandClick.subscribe((e: any) => this.gridOnCommandClick(e)));
    this.manage(g.recordClick.subscribe((e: any) => this.gridOnRecordClick(e)));
    this.manage(g.recordDoubleClick.subscribe((e: any) => this.gridOnRecordDoubleClick(e)));
    this.manage(g.toolbarClick.subscribe((e: any) => this.gridOnToolbarClick(e)));
    this.manage(g.contextMenuOpen.subscribe((e: any) => this.gridOnContextMenuOpen(e)));
    this.manage(g.contextMenuClick.subscribe((e: any) => this.gridOnContextMenuClick(e)));
    this.manage(g.beforeOpenAdaptiveDialog.subscribe((e: any) => this.gridOnBeforeOpenAdaptiveDialog(e)));
    this.manage(g.beforeOpenColumnChooser.subscribe((e: any) => this.gridOnBeforeOpenColumnChooser(e)));
    this.manage(g.queryCellInfo.subscribe((e: any) => this.gridOnQueryCellInfo(e)));
    this.manage(g.cellEdit.subscribe((e: any) => this.gridOnCellEdit(e)));
    this.manage(g.headerCellInfo.subscribe((e: any) => CerGridCommandService.gridOnHeaderCellInfo(e)));
    this.manage(g.beforeExcelExport.subscribe((e: any) => this.gridOnBeforeExcelExport(e)));
    this.manage(g.excelExportComplete.subscribe((e: any) => this.gridOnAfterExcelExport(e)));
    this.manage(g.excelQueryCellInfo.subscribe((e: any) => this.gridOnExcelQueryCellInfo(e)));
    this.manage(g.excelHeaderQueryCellInfo.subscribe((e: any) => this.gridOnExcelQueryHeaderCellInfo(e)));

    const gAny: any = g;
    gAny.on("finalPageSetup", (e: any) => this.gridOnExcelFinalPageSetup(e));


    if (this.data) {
      if (!this.data.dataManager) {
        this.manage(this.data.data$.subscribe(data => this.gridOnDataLoad(data)));
      }
      this.manage(this.data.fkDataLoaded$.subscribe(dataLoaded => { if (dataLoaded) { this.gridOnDataFKLoad(); } }));
    }
  }

  private gridOff() {
    const gAny: any = this.grid;
    gAny.off("finalPageSetup", (e: any) => this.gridOnExcelFinalPageSetup(e));
  }

  private gridOnLoad(args: any) {
    this.cmdDispatch(UiCommandSourceEnum.ActionComplete, null, args, null, UiActionTypeEnum.Loaded);
  }

  private gridOnCreated(args: any) {
    this.cmdDispatch(UiCommandSourceEnum.ActionComplete, null, args, null, UiActionTypeEnum.Created);
  }

  private gridOnDestroyed(args: any) {
    this.cmdDispatch(UiCommandSourceEnum.ActionComplete, null, args, null, UiActionTypeEnum.Destroyed);
  }

  private gridOnDataStateChange(args: any) {
    this.cmdDispatch(UiCommandSourceEnum.ActionComplete, null, args, null, UiActionTypeEnum.DataStateChange);
  }

  private gridOnDataLoad(data: object[]) {
    this.cmdDispatch(UiCommandSourceEnum.ActionComplete, null, data, '', UiActionTypeEnum.DataLoad, null);
  }

  private gridOnDataFKLoad() {
    this.cmdDispatch(UiCommandSourceEnum.ActionComplete, null, null, '', UiActionTypeEnum.DataFKLoad, null);
  }

  private gridOnDataBoundBegin(args: any) {
    if (args.count === undefined) {
      args.count = 0;
      args.result = [];
    }
    this.onDataBoundActionBegin(args);
    this.cmdDispatch(UiCommandSourceEnum.ActionBegin, null, args, null, UiActionTypeEnum.DataBound);
  }

  protected gridOnDataBoundComplete(args: any) {
    this.cmdDispatch(UiCommandSourceEnum.ActionComplete, null, args, null, UiActionTypeEnum.DataBound, this.grid.currentViewData);
  }

  protected gridOndataSourceChangedComplete(args: any) {
    this.cmdDispatch(UiCommandSourceEnum.ActionComplete, null, args, null, UiActionTypeEnum.DataSourceChanged);
  }

  private gridOnActionFailure(e: any) {
    this.cmdDispatch(UiCommandSourceEnum.ActionComplete, null, e, '', UiActionTypeEnum.ActionFailure);
  }

  private gridOnBeforeBatchAdd(e: BeforeBatchAddArgs) {
    this.cmdDispatch(UiCommandSourceEnum.ActionBegin, null, e, '', UiActionTypeEnum.BatchAdd);
    this.cmdDispatch(UiCommandSourceEnum.ActionBegin, null, e, '', UiActionTypeEnum.Add);
  }

  private gridOnBeforeBatchDelete(e: BeforeBatchDeleteArgs) {
    this.cmdDispatch(UiCommandSourceEnum.ActionBegin, null, e, '', UiActionTypeEnum.BatchDelete);
    //    this.cmdDispatch(UiCommandSourceEnum.ActionBegin, null, e, '', UiActionTypeEnum.Delete);
  }

  private gridOnBeforeBatchSave(e: BeforeBatchSaveArgs) {
    this.cmdDispatch(UiCommandSourceEnum.ActionBegin, null, e, '', UiActionTypeEnum.BatchSave);
    this.cmdDispatch(UiCommandSourceEnum.ActionBegin, null, e, '', UiActionTypeEnum.Save);
  }

  private gridOnAfterBatchAdd(e: BatchAddArgs) {
    this.cmdDispatch(UiCommandSourceEnum.ActionBegin, null, e, '', UiActionTypeEnum.BatchAdd);
    this.cmdDispatch(UiCommandSourceEnum.ActionBegin, null, e, '', UiActionTypeEnum.Add);
  }

  private gridOnAfterBatchDelete(e: BatchDeleteArgs) {
    this.cmdDispatch(UiCommandSourceEnum.ActionBegin, null, e, '', UiActionTypeEnum.BatchDelete);
    //this.cmdDispatch(UiCommandSourceEnum.ActionBegin, null, e, '', UiActionTypeEnum.Delete);
  }

  private gridOnAfterBatchCancel(e: any) {
    this.cmdDispatch(UiCommandSourceEnum.ActionComplete, null, e, '', UiActionTypeEnum.BatchCancel);
    this.cmdDispatch(UiCommandSourceEnum.ActionComplete, null, e, '', UiActionTypeEnum.Cancel);
  }

  private gridRequestTypeToActionType(requestType: string): UiActionTypeEnum {
    var actionType = enumFromString(UiActionTypeEnum, requestType);
    if (actionType === undefined) {
      console.info("**** Warning: Unhandled action type: " + requestType);
    }
    return actionType;
  }

  public gridOnActionBegin(args: any) {
    var actionType = this.gridRequestTypeToActionType(args.requestType);
    var columnName = args.column?.field;
    var rowData = args.data ?? args.rowData;
    var rowDataOriginal = args.previousData;
    var subAction: CerEventSubAction = 'none';
    switch (args.action) {
      case 'edit':
        subAction = 'edit';
        break;
      case 'add':
        subAction = 'add';
        break;
    }
    this.cmdDispatch(UiCommandSourceEnum.ActionBegin, null, args, columnName, actionType, null, rowData, null, subAction, rowDataOriginal);
  }

  private gridOnActionComplete(args: any) {
    var actionType = this.gridRequestTypeToActionType(args.requestType);

    var subAction: CerEventSubAction = 'none';
    switch (args.action) {
      case 'edit':
        subAction = 'edit';
        break;
      case 'add':
        subAction = 'add';
        break;
    }
    var columnName = args.column?.field ?? args.columnName;
    var rowData = args.rowData;
    var rowsData = args.rows;
    var rowDataOriginal = args.rowDataOriginal;

    this.cmdDispatch(UiCommandSourceEnum.ActionComplete, null, args, columnName, actionType, rowsData, rowData, null, subAction, rowDataOriginal);
  }

  private gridOnCellSelecting(args: any) {
    this.cmdDispatch(UiCommandSourceEnum.ActionComplete, null, args, null, UiActionTypeEnum.CellSelecting, [args.data], args.data);
  }

  private gridOnCellSelected(args: CellSelectEventArgs) {
    this.commandsEnableBySelection(true);
    const columnName: string = this.grid.getColumnByIndex(args.cellIndex.cellIndex)?.field;
    this.cmdDispatch(UiCommandSourceEnum.ActionComplete, null, args, columnName, UiActionTypeEnum.CellSelect, [args.data], args.data);
  }

  private gridOnCellDeselected(args: any) {
    this.commandsEnableBySelection(this.appGrid.rowSelectedActive());
    this.cmdDispatch(UiCommandSourceEnum.ActionComplete, null, args, null, UiActionTypeEnum.CellDeselect, [args.data], args.data);
  }

  private gridOnRowSelecting(args: any) {
    this.cmdDispatch(UiCommandSourceEnum.ActionComplete, null, args, null, UiActionTypeEnum.RowSelecting, [args.data], args.data);
  }

  private gridOnRowSelected(args: any) {
    this.commandsEnableBySelection(true);
    this.cmdDispatch(UiCommandSourceEnum.ActionComplete, null, args, this.columnNameFromTarget(args.target), UiActionTypeEnum.RowSelect, [args.data], args.data);
  }

  private gridOnRowDeselected(args: any) {
    this.commandsEnableBySelection(this.appGrid.rowSelectedActive());
    this.cmdDispatch(UiCommandSourceEnum.ActionComplete, null, args, null, UiActionTypeEnum.RowDeselect, [args.data], args.data);
  }

  private gridOnCommandClick(args: any) {
    var rowsData = this.appGrid.rowsSelectedData();
    this.cmdDispatch(UiCommandSourceEnum.Command, null, args, args.column?.name, UiActionTypeEnum.CommandClicked, rowsData, args.rowData);
  }

  private gridOnRecordClick(args: any) {
    var columnName = args.column?.field;
    var rowsData = this.appGrid.rowSelectedData();
    var rowData = args.rowData;
    this.cmdDispatch(UiCommandSourceEnum.ActionBegin, null, args, columnName, UiActionTypeEnum.RowClick, null, rowData);
  }

  private columnNameFromTarget(target: HTMLElement): string {
    var colIdx = target?.attributes.getNamedItem("data-colindex")?.value;
    while (colIdx == null && target?.parentElement != null &&  target.parentElement.tagName != 'td') {
      target = target.parentElement;
      colIdx = target?.attributes.getNamedItem("data-colindex")?.value;
    }
    const idx: number = colIdx?.length > 0 ? parseInt(colIdx, 10) : null;
    return idx != null ? this.grid.getColumnByIndex(idx)?.field : null;
  }

  private gridOnRecordDoubleClick(args: any) {
    var columnName = args.column?.field;
    var rowsData = this.appGrid.rowsSelectedData();
    var rowData = args.rowData;
    this.cmdDispatch(UiCommandSourceEnum.ActionBegin, null, args, columnName, UiActionTypeEnum.RowDoubleClick, rowsData, rowData);
  }

  private gridOnToolbarClick(args: any) {
    args.rowData = this.appGrid.rowSelectedData();
    var rowsData = this.appGrid.rowsSelectedData();
    var command: UiCommand = (<any>args.item)?.cerUiCommand;
    this.cmdDispatch(UiCommandSourceEnum.Toolbar, args?.item?.id, args, null, UiActionTypeEnum.ToolbarMenuItem, rowsData, args.rowData, command);
  }

  private gridOnContextMenuOpen(args: any) {
    args.event = args.event ?? this.touchEventArgs;  // Touch event arguments are passed as event property
    if (this.isDisabled()) {
      args.cancel = true;
    }
    this.cmdDispatch(UiCommandSourceEnum.ContextMenu, args.id, args, args.column?.field, UiActionTypeEnum.ContextMenuOpen, this.grid.currentViewData, args.rowInfo.rowData, (<any>args.item)?.command);

    this.contextMenuItemsHideSeperators(args.items as MenuItem[]);
    if (args.rowInfo && !this.contextMenuItemsIsActive(args.rowInfo.target)) {
      args.cancel = true;
    }
  }

  public gridOnContextMenuClick(args: ContextMenuClickEventArgs) {
    var command: UiCommand = (<any>args.item).cerUiCommand;
    this.cmdDispatch(UiCommandSourceEnum.ContextMenu, args.item.id, args, args.column?.field, UiActionTypeEnum.ContextMenuItem, this.grid.currentViewData, args.rowInfo?.rowData, command);
  }

  private gridOnBeforeOpenColumnChooser(args: any) {
    this.cmdDispatch(UiCommandSourceEnum.ActionBegin, args.id, args, null, UiActionTypeEnum.BeforeOpenColumnChooser);
  }

  private gridOnBeforeOpenAdaptiveDialog(args: any) {
    this.cmdDispatch(UiCommandSourceEnum.ActionBegin, args.id, args, args.column ? args.column.field : null, UiActionTypeEnum.BeforeOpenAdaptiveDialog);
  }

  private gridOnBeforeExcelExport(args: any) {
    this.cmdDispatch(UiCommandSourceEnum.ActionBegin, args.id, args, args.column ? args.column.field : null, UiActionTypeEnum.ExcelExport);
  }

  private gridOnAfterExcelExport(args: any) {
    this.cmdDispatch(UiCommandSourceEnum.ActionComplete, args.id, args, args.column ? args.column.field : null, UiActionTypeEnum.ExcelExport);
  }

  public onExcelCellEventEnabled: boolean = false;
  public onExcelCellFieldNames: string[] = [];
  private gridOnExcelQueryCellInfo(args: any) {
    if (this.onExcelCellEventEnabled) {
      const columnName: string = args.column?.field;
      if (this.onExcelCellFieldNames.includes(columnName)) {
        const meta: FieldMetadata = FieldMetadataUtil.fieldMetaByColumn(args.column);
        if (meta != null) {
          const e: UiCommandRenderCell = {
            //args: args,
            data: args.data,
            column: args.column,
            columnName: columnName,
            fieldMeta: meta,
            foreignKeyData: args.foreignKeyData == true,
            cell: args.cell,
            value: (args as any).value ?? FieldMetadataUtil.valueGet<number>(args.data, meta),
            style: args.style,
            colSpan: args.colSpan,
            rowSpan: args.rowSpan,
            isExcel: true
          };
          this.cmdDispatch(UiCommandSourceEnum.ActionBegin, args.id, e, columnName, UiActionTypeEnum.ExcelExportCell);
          args.cell = e.cell;
          args.style = e.style;
          args.colSpan = e.colSpan;
        }
      }
    }
  }

  private gridOnExcelQueryHeaderCellInfo(args: any) {
    this.cmdDispatch(UiCommandSourceEnum.ActionBegin, args.id, args, args.column ? args.column.field : null, UiActionTypeEnum.ExcelExportHeaderCell);
  }

  private gridOnExcelFinalPageSetup(args: any) {
    this.cmdDispatch(UiCommandSourceEnum.ActionBegin, args.id, args, args.column ? args.column.field : null, UiActionTypeEnum.ExcelExportFinalPageSetup);
  }

  private gridOnCellEdit(args: EditEventArgs) {
    //this.cellEditFields.filter(f => f.field == args.column.field)
    //.forEach(f => f.onCellEdit(args));
  }

  // Header cell info
  public static gridOnHeaderCellInfo(args: any) {
    if (args.node == null) {
      return;
    }
    // Enable format header using flex box
    const cellDiv = args.node.querySelector("div.e-headercelldiv") as HTMLDivElement;
    const sortDiv = args.node.querySelector("div.e-sortfilterdiv") as HTMLDivElement;
    const filterDiv = args.node.querySelector("div.e-filtermenudiv") as HTMLDivElement;
    if (cellDiv != null) {
      if (sortDiv != null) {
        sortDiv.parentNode.removeChild(sortDiv);
        cellDiv.appendChild(sortDiv);
      }
      if (filterDiv != null) {
        filterDiv.parentNode.removeChild(filterDiv);
        cellDiv.appendChild(filterDiv);
      }
    }

    // Add tooltip
    if (args.cell?.column) {
      var column = args.cell.column;
      if (column.toolTipShow?.includes('Header')) {
        var fieldMeta: FieldMetadata = column.meta as FieldMetadata;
        if (fieldMeta?.tooltipText?.length > 0 && fieldMeta.tooltipText != fieldMeta.text) {
          var tooltip = new Tooltip({
            position: 'TopCenter',
            content: fieldMeta.tooltipText,
            opensOn: 'Auto',
            openDelay: 1000
          });
          tooltip.appendTo(args.node);
        }
      }
    }
  }

  // Query cell info
  public onRenderCellEventEnabled: boolean = false;
  private gridOnQueryCellInfo(args: QueryCellInfoEventArgs) {
    if (this.onRenderCellEventEnabled) {
      this.cmdDispatch(UiCommandSourceEnum.ActionBegin, null, args, null, UiActionTypeEnum.QueryCellInfo);
    }
  }

  public onChatColumnLinkClicked(args: any) {
    let rowInfo = this.grid.getRowInfo(args.target); // Get row information
    args.rowInfo = rowInfo;
    var rowData = rowInfo.rowData;
    this.cmdDispatch(UiCommandSourceEnum.ActionComplete, null, args, 'chat', UiActionTypeEnum.ChatClicked, this.grid.currentViewData, rowData);
  }

  /***************
   * Touch events
   ***************/

  //private touchContextMenuOpenFn: any;

  // Grid’s created event handler
  private touchInit() {
    // Since the Grid element is the context menu’s target, the events are bound to the Grid element
    var options: any = { passive: true };
    //    this.grid.element.addEventListener('touchstart', this.onTouchStart.bind(this), options);
    //    this.grid.element.addEventListener('touchend', this.onTouchEnd.bind(this), options);

    //this.touchContextMenuOpenFn = this.grid.contextMenuModule.contextMenu.beforeOpen;
    //this.grid.contextMenuModule.contextMenu.beforeOpen.bind(this.touchContextMenuOpen);
  }

  private touchDestroy() {
    var options: any = { passive: true };

    //    this.grid.element.removeEventListener('touchstart', this.onTouchStart.bind(this), options);
    //    this.grid.element.removeEventListener('touchend', this.onTouchEnd.bind(this), options);
  }

  /*
  private touchContextMenuOpen(e: any) {
    // The Grid context menu beforeOpen event is overridden in order to pass the touch event arguments to is
    // The event arguments stored from 'onlongtouch' event is stored to the event property and the source method is called
    this.touchContextMenuOpenFn.call(this, e);
  }
  */

  private touchTimer: any = null;
  private onTouchStart(e: any) {
    e.preventDefault();
    if (!this.touchTimer) {
      var longPressDuration: number = 500;
      this.touchTimer = setTimeout(this.onTouchLongTouch.bind(this, e), longPressDuration);
    }
  }

  private onTouchEnd(e: any) {
    if (this.touchTimer) {
      clearTimeout(this.touchTimer);
      this.touchTimer = null;
    }
  }

  private touchEventArgs: any;  // Read in gridOnContextMenuOpen(), written in onTouchLong()
  private onTouchLongTouch(e: any) {
    this.touchTimer = null;
    this.touchEventArgs = e; // Store event arguments to be used in context menu open event
    this.grid.contextMenuModule.contextMenu.open(e.touches[0].pageY, e.touches[0].pageX);
  }

  /******************************
  * Toolbar + context menu state
  ******************************/

  public commandVisible(commandId: string, visible: boolean) {
    this.grid.toolbar?.forEach(i => {
      if (typeof i === "object" && i.id == commandId) {
        this.toolbarItemVisible(i, visible);
      }
    });
    this.grid.contextMenuItems?.forEach(i => {
      if (typeof i === "object" && i.id == commandId) {
        this.contextMenuVisible(i, visible)
      }
    });
  }

  public commandEnable(commandId: string, enabled: boolean) {
    this.grid.toolbar?.forEach(i => {
      if (typeof i === "object" && i.id == commandId) {
        this.toolbarItemEnable(i, enabled);
      }
    });
    this.grid.contextMenuItems?.forEach(i => {
      if (typeof i === "object" && i.id == commandId) {
        this.contextMenuEnable(i, enabled)
      }
    });
  }

  private commandsEnableBySelection(selectionActive: boolean, command: string = null) {
    this.grid.toolbar?.forEach(i => this.toolbarItemEnableBySelection(i, selectionActive));
    this.grid.contextMenuItems?.forEach(i => this.contextMenuEnableBySelection(i, selectionActive));
  }

  /*********
  * Toolbar 
  *********/

  private gridToolbar: (ToolbarItems | string | ItemModel | ToolbarItem)[] =
    [
      this.toolbarItemCreate(uiCmdResearch),
      this.toolbarItemCreate(uiCmdColumnsChoose),
      //this.toolbarItemCreate(uiCmdClearFilter),
      this.toolbarItemCreate(uiCmdExportExcel),
      'Edit',
      'Update',
      'Cancel',
      'Add',
      'Delete',
      'Search'
    ];

  public toolbarReflow() {
    if (this.grid && this.grid.toolbarModule && this.grid.toolbarModule.toolbar) {
      this.grid.toolbarModule.toolbar.refreshOverflow();
    }
  }

  private toolbarInit() {
    this.toolbarCardVievInit();
    this.toolbarCRUDInit()
    this.toolbarTitleInit()
    this.toolbarNavigationsAdd();
    this.toolbarCommandsAdd([uiCmdComponentMaximize])

    this.gridToolbar = this.gridToolbar.filter(
      mi => this.toolbarSetupItemVisible(mi)
    );

    this.grid.toolbar = this.gridToolbar;
  }

  private toolbarInitComplete() {
    if (this.grid?.toolbarModule?.toolbar) {
      this.grid.toolbarModule.toolbar.overflowMode = 'Popup';
    }

    var type: CerGridToolbarType = this.design.toolbarType;
    var isDefault: boolean = (type == "default");
    var isSearch: boolean = (type == 'search');
    var isMinimal: boolean = (type == 'minimal');

    var hideStandard: boolean = (!isDefault);
    //var moveToPopup: boolean = (isMinimal || isSearch);

    var hideOnOverflowPrefixIcon: string[] = ['e-edit', 'e-add', 'e-delete'];
    //var moveToDropDownPrefixIcon: string[] = ['e-edit', 'e-add', 'e-delete'];

    this.grid?.toolbarModule?.toolbar?.items?.forEach(i => {
      this.toolbarItemInitComplete(isSearch, i, hideStandard, hideOnOverflowPrefixIcon)
    });
    this.toolbarReflow();
  }

  private toolbarItemInitComplete(isSearch: boolean, i: ItemModel, hideStandard: boolean, hideOnOverflowPrefixIcon: string[]) {
    if (isSearch && (i.id.endsWith('_search') || i.id == 'uiCmdToolbarTitle')) {
      i.showTextOn = 'Toolbar';
      if (i.id.endsWith('_search')) {
        i.align = 'Left';
      }
    }
    else if (hideStandard) {
      if (hideOnOverflowPrefixIcon.includes(i.prefixIcon)) {
        i.showTextOn = 'Overflow';
      }
      if (i.suffixIcon == 'e-resfilter-icon') {
        i.visible = false;
      }
    }
    switch (i.prefixIcon) {
      case 'e-edit':
        i.tooltipText = 'Rediger valgt post [F2]';
        break;
      case 'e-add':
        i.tooltipText = 'Tilføj ny post [Insert]';
        break;
      case 'e-delete':
        i.tooltipText = 'Slet valgt post [Delete]';
        break;
    }
    // i.showAlwaysInPopup = true
  }

  public toolbarItemRemove(menuItem: any) {
    this.gridToolbar = this.gridToolbar.filter(mi => mi !== menuItem);
  }

  public toolbarItemCreate(command: UiCommand): ItemModel {
    return CerToolbarCommandAdapter.itemCreate(command);
  }

  public toolbarCommandsAdd(customCmd: UiCommand[]) {
    if (customCmd?.length > 0) {
      var t: any[] = this.gridToolbar;
      customCmd.filter(c => c.toolbarPosition == null || c.toolbarPosition == 'next').reverse().forEach(c => {
        t.unshift(this.toolbarItemCreate(c))
      });
      customCmd.filter(c => c.toolbarPosition == 'first').reverse().forEach(c => {
        t.unshift(this.toolbarItemCreate(c))
      });
      customCmd.filter(c => c.toolbarPosition == 'last').forEach(c => {
        t.push(this.toolbarItemCreate(c))
      });
    }
  }

  private toolbarItemEnableBySelection(item: (string | ItemModel | ToolbarItem), selectionActive: boolean) {
    if (typeof item === "object") {
      if ((<any>item).needSelection === true) {
        this.toolbarItemEnable(item.id, selectionActive);
      }
    }
  }

  public toolbarCommandVisible(cmd: UiCommand, visible: boolean) {
    if (cmd?.text?.length > 0 ) {
      const element : HTMLElement = this.toolbarElements(true).find((e: Element) => (e as HTMLElement)?.innerText == cmd.text) as HTMLElement;
      if (element) {
        if (visible) {
          element.classList.remove('e-hidden');
        }
        else {
          element.classList.add('e-hidden');
        }
      }
    }
  }

  public toolbarItemVisible(item: (string | ItemModel | ToolbarItem), visible: boolean) {
    if (typeof item === "object") {
      this.grid.toolbarModule.enableItems([item.id], visible);
      //tBar.hideItem(0, visible);
    }
  }

  private toolbarItemEnable(item: (string | ItemModel | ToolbarItem), enable: boolean) {
    if (typeof item === "object") {
      this.grid.toolbarModule.enableItems([item.id], enable);
    }
  }

  public toolbarElements(all: boolean): Element[] {
    //var classes: string = '.e-toolbar-item:not(.e-overlay):not(.e-hidden)';
    const classes = '.e-toolbar-item' + (all ? '' : ':not(.e-hidden)');
    return [].slice.call(this.grid.toolbarModule.element.querySelectorAll(classes));
  }

  public toolbarElementGet(title: string): HTMLDivElement {
    var toolbarItem: HTMLDivElement = null;
    var toolbarItems: Element[] = this.toolbarElements(false);
    toolbarItems.forEach(item => {
      if (item && (<HTMLDivElement>item).title == title) {
        toolbarItem = <HTMLDivElement>item;
      }
    });
    return toolbarItem;
  }

  public toolbarButtonGet(title: string): HTMLButtonElement {
    var toolbarItem: HTMLDivElement = this.toolbarElementGet(title);
    var buttons: HTMLCollection = toolbarItem?.getElementsByTagName('button');
    return <HTMLButtonElement>(buttons != null && buttons.length > 0 ? <HTMLButtonElement>buttons[0] : null);
  }

  public toolbarButtonTextSet(button: HTMLButtonElement, text: string) {
    if (button) {
      button.title = text;
      var txts = button.getElementsByClassName('e-tbar-btn-text');
      if (txts != null && txts.length > 0) {
        (<HTMLSpanElement>txts[0]).innerText = text;
      }
    }
  }

  public toolbarSetupItemVisible(mi: ToolbarItems | string | ItemModel | ToolbarItem): boolean {
    var visible: boolean = true;

    if (['simple', 'minimal'].includes(this.design.toolbarType)) {
      visible = this.toolbarSetupItemVisibleSimple(mi, visible);
    }

    return visible;
  }

  private toolbarSetupItemVisibleSimple(mi: string | ItemModel | ToolbarItem, visible: boolean) {
    if (typeof mi === 'string') {
      switch (<string>mi) {
        case 'Search':
          visible = false;
          break;
      }
    }
    else if ((<any>mi).id) {
      switch ((<any>mi).id) {
        case uiCmdResearch.id:
        case uiCmdExportExcel.id:
        case uiCmdResearch.id:
        case uiCmdFilterClear.id:
          visible = false;
          break;
        /*case uiCmdColumnsChoose.id:
          if (!this.design.card) {
            visible = false;
          }*/
      }
    }
    return visible;
  }

  private toolbarCardVievInit() {
    //if (this.design.card) {
    //  this.gridToolbar.push(this.toolbarItemCreate(Object.assign({}, uiCmdColumnsChoose, { target: null })));
    //}
  }

  public toolbarCRUDInit() {
    const viewMeta = this.meta.viewMetadata;
    if (!viewMeta) {
      return;
    }
    if (viewMeta.allowEdit == false || this.design.editSettings?.mode == 'inlineBatch') {
      this.toolbarItemRemove('Edit');
    }
    if (viewMeta.allowCreate == false) {
      this.toolbarItemRemove('Add');
    }
    if (viewMeta.allowDelete == false) {
      this.toolbarItemRemove('Delete');
    }
    if ((viewMeta.allowCreate == false && viewMeta.allowEdit == false) || (this.grid.editSettings.mode == "Dialog")) {
      this.toolbarItemRemove('Cancel');
      this.toolbarItemRemove('Update');
    }
  }

  // Navigation
  private toolbarNavigationsAdd() {
    if (this.data?.viewMetadata) {
      const parentView: CerFormView = this.appGrid?.formView ?? CerFormView.formViewByComponent(this.appGrid);
      const navs = this.formNavigationService.navigationsGet(this.data.viewMetadata, parentView);
      const navCmds: UiCommand[] = [];
      this.navMenuCmds = [];
      navs?.forEach(nav => {
        const cmd: UiCommand = Object.assign({}, uiCmdNavigateItem);
        cmd.text = nav.title ?? nav.url ?? "Navigation";
        cmd.data = nav;
        if (nav.link == 'nav' || (Array.isArray(nav.link) && (nav.link.includes('nav')))) {
          cmd.toolbarPosition = 'first';
          navCmds.push(cmd);
        }
        else {
          cmd.iconCss = null;
          this.navMenuCmds.push(cmd);
        }
      })
      if (this.navMenuCmds.length > 0) {
        const uiCmdNav: UiCommand = Object.assign({}, uiCmdNavigate)
        this.toolbarCommandsAdd([uiCmdNav]);
      }
      if (navCmds.length > 0) {
        this.toolbarCommandsAdd(navCmds);
      }
    }
  }

  private navMenuCmds: UiCommand[] = [];
  public navigationDropDownMenuRender(event: UiCommandEvent) {
    if (this.navMenuCmds?.length > 0) {
      const navBtn: HTMLButtonElement = this.toolbarButtonGet(uiCmdNavigate.text);
      if (navBtn) {
        this.dropDownMenuRender(navBtn, this.navMenuCmds);
      }
    }
  }

  private navigationDropDownButton: DropDownButton;
  public navigationDownMenuDestroy() {
    if (this.navigationDropDownButton) {
      this.navigationDropDownButton.destroy();
      this.navigationDropDownButton = null;
    }
  }

  public dropDownMenuRender(parent: HTMLButtonElement, cmds: UiCommand[]) {
    if (this.navigationDropDownButton != null) {
      return;
    }
    const items = cmds.map(c =>
      this.contextMenuItemCreate(c)
    );

    parent.id = this.appGrid.id + '-navigate-btn';

    this.navigationDropDownButton = new DropDownButton({
      items: items,
      enableRtl: this.grid.enableRtl,
      enablePersistence: this.grid.enablePersistence,
      locale: this.grid.locale,
      select: this.onNavigationMenuClick.bind(this),
      cssClass: 'e-grid-menu'
    });
    this.navigationDropDownButton.appendTo(parent);
  }

  private onNavigationMenuClick(args: any) {
    this.gridOnToolbarClick(args);
  }

  // Toolbar title
  public gridToolbarItemTitleText: string;

  private toolbarTitleInit() {
    const text = this.meta?.viewMetadata?.text;
    if (this.design?.toolbarTitle != 'disabled' && text?.length > 0) {
      this.gridToolbarItemTitleText = text;
      this.gridToolbar.push(CerToolbarCommandAdapter.itemCreateTitle(text));
    }
  }

  private onDataBoundActionBegin(args: any) {
    if (args.action !== 'edit') {
      var rowCount: number = args.count;
      var pageIndex: number = 0;
      var pageSize: number = 0;

      var q = args?.query?.queries;
      if (q?.length > 0) {
        var f = args.query.queries.find((f: any) => f.fn == 'onPage' && f.e?.pageIndex > 0 && f.e?.pageSize > 0);
        if (f) {
          pageIndex = f.e.pageIndex;
          pageSize = f.e.pageSize;
        }
      }
      //this.debug('records read: ' + pageSize + '*' + pageIndex + ' total ' + rowCount);
      this.toolbarTitleUpdateCount(pageSize * pageIndex, rowCount);
    }
  }

  private fetchPct: number = 0;
  private rowsTotal: number = 0;

  private toolbarTitleResetCount() {
    this.fetchPct = 0;
    this.rowsTotal = 0;
  }

  public toolbarTitleUpdateCount(rowsFetched: number, rowsTotal: number) {

    var fetchPct: number = rowsTotal ? Math.min(Math.trunc(rowsFetched * 100 / rowsTotal), 100) : 100;
    if (fetchPct >= this.fetchPct || rowsTotal != this.rowsTotal) {
      this.fetchPct = fetchPct;
      this.rowsTotal = rowsTotal;
      this.toolbarTitleUpdate(this.fetchPct, this.rowsTotal);
    }
  }

  private toolbarTitleUpdate(fetchPct: number, rowsTotal: number) {
    if (this.design?.toolbarTitle != 'disabled' && toolbarItemTitle.id) {
      var title: string = this.gridToolbarItemTitleText;
      var showTitle: boolean = ['metadata', 'metadata-count'].includes(this.design?.toolbarTitle) && title?.length > 0;
      var showCount: boolean = ['metadata-count', 'count'].includes(this.design?.toolbarTitle);
      var tooltip = title + ': ' + rowsTotal + ' ' + (rowsTotal == 1 ? 'post' : 'poster');
      var buttonTxt: string = showTitle ? title : "";
      if (showCount) {
        var total: string = rowsTotal ? rowsTotal.toString() : null;
        buttonTxt = (buttonTxt ? buttonTxt + (total ? " (" + total + ")" : "") : total);
      }
      buttonTxt = buttonTxt.trim();
      //var style = this.toolbarTitleUpdateCountStyle(fetchPct);
      var button = this.grid.element.querySelector('#' + toolbarItemTitle.id);
      CerToolbarCommandAdapter.itemTitleButtonSetup(button, buttonTxt, tooltip);
      if (this.grid?.toolbarModule?.toolbar) {
        this.grid.toolbarModule.enableItems([toolbarItemTitle.id], true);
      }
    }
  }

  /***************
   * Context menu
   ***************/

  public contextMenuOpen(e: any) {
    this.onContextMenuOpenNavigate(e);
    this.grid.contextMenuModule.contextMenu.open(e.clientY, e.clientX);
  }

  private onContextMenuOpenNavigate(e: UiCommandEvent) {
    const target = e.args.rowInfo.target;
    const isHeader = target && parentsUntil(target, 'e-headercontent', false) != null;
    const menuItems: MenuItem[] = e.args.items as MenuItem[];

    const nav = this.formNavigationService.navigationFromUiCommandColumnFK(e, !isHeader);
    const visible: boolean = (nav?.length > 0);
    menuItems?.filter(i => [uiCmdColumnNavigateFK.id, uiCmdColumnNavigateFKRow.id].includes(i.id))
      .forEach(i => {

        const itemVisible = visible && this.contextMenuItemIsActive(i, target);
        this.contextMenuVisible(i, itemVisible);
        const idx = menuItems.indexOf(i);
        if (idx > 0) {
          const iSep = menuItems[idx - 1];
          if (iSep.separator == true) {
            this.contextMenuVisible(iSep, itemVisible);
          }
        }
      });
  }

  public contextMenuEnableBySelection(item: any, selectionActive: boolean) {
    if (typeof item === "object") {
      if (item.needSelection === true) {
        this.contextMenuEnable(item, selectionActive);
      }
    }
  }

  public contextMenuEnable(item: any, enable: boolean) {
    if (typeof item === "object") {
      this.grid.contextMenuModule.contextMenu.enableItems([item.id], enable, true);
    }
  }

  public contextMenuVisible(item: any, visible: boolean) {
    visible ? this.contextMenuShow(item) : this.contextMenuHide(item);
  }

  public contextMenuClose() {
    this.grid.contextMenuModule.contextMenu.close();
    this.grid.contextMenuModule.isOpen = false;
  }

  public contextMenuShow(item: any) {
    if (item != null && typeof item === "object") {
      var ctxMenu = this.grid.contextMenuModule.contextMenu;
      ctxMenu.showItems([item.id], true);
    }
  }

  public contextMenuHide(item: any) {
    if (item != null && typeof item === "object") {
      var ctxMenu = this.grid.contextMenuModule.contextMenu;
      ctxMenu.hideItems([item.id], true);
    }
  }

  private contextMenuItemsHideSeperators(menuItems: MenuItem[]) {

    const ctxMenu = this.grid.contextMenuModule.contextMenu;
    const classHide: string = 'e-menu-hide';
    const classSeparator: string = 'e-separator';

    const list = (<any>ctxMenu).getWrapper()?.children;
    for (var i = 0; i < list.length; i++) {
      {
        const items = list[i].children;
        var prevHidden = true;
        var firstVisible = true;
        var previous: HTMLLIElement = null;
        var lastVisible: HTMLLIElement = null;
        for (var j = 0; j < items.length; j++) {
          var li: HTMLLIElement = items[j];
          if (li.classList.contains(classHide)) {
            prevHidden = true;
          }
          else {
            if (li.classList.contains(classSeparator)) {
              // Hide if first is seperator or double separators
              if (firstVisible || previous?.classList.contains(classSeparator)) {
                li.classList.add(classHide);
              }
            }
            firstVisible = false;
            prevHidden = false;
            lastVisible = li;
            previous = li;
          }
        }
        if (lastVisible?.classList.contains(classSeparator)) {
          lastVisible.classList.add(classHide);
        }
      }
    }
  }

  public contextMenuItemCreate(cmd: UiCommand, target: string = null): ContextMenuItemModel {
    if (cmd.separator == 'header') {
      return this.gridContextMenuSeparatorHeader;
    }
    if (cmd.separator == 'content') {
      return this.gridContextMenuSeparatorContent;
    }
    var i: ContextMenuItemModel = { id: cmd.id, text: cmd.text, iconCss: cmd.iconCss, target: target ? target : cmd.target };
    if (cmd.needSelection) {
      (<any>i).needSelection = true;
    }
    (<any>i).cerUiCommand = cmd;
    return i;
  }

  public contextMenuCommandsInit(customCmd: UiCommand[]) {
    if (customCmd?.length > 0) {
      // Header
      var header = customCmd.filter(c => (c.target == '.e-header' || c.separator == 'header') && c.visible !== false);
      if (header.length > 0) {
        this.gridContextMenuItemsHeader.unshift(this.gridContextMenuSeparatorHeader);
        header.reverse().forEach(c => this.gridContextMenuItemsHeader.unshift(this.contextMenuItemCreate(c)));
      }
    }

    // Content
    if (this.grid.allowFiltering) {
      this.gridContextMenuItemsContent = this.gridContextMenuItemsContentFiltering.concat(this.gridContextMenuItemsContent);
    }
    if (customCmd?.length > 0) {
      var content = customCmd.filter(c => (c.target == '.e-content' || c.separator == 'content') && c.visible !== false);
      if (content.length > 0) {
        this.gridContextMenuItemsContent.unshift(this.gridContextMenuSeparatorContent);
        content.reverse().forEach(c => this.gridContextMenuItemsContent.unshift(this.contextMenuItemCreate(c)));
      }
    }

    // Content - edit
    if (this.grid.editSettings.allowEditing == true) {
      const viewMeta = this.meta.viewMetadata;
      const title = viewMeta?.textSingular?.toLocaleLowerCase();
      if (title?.length > 0) {
        this.gridContextMenuItemsEdit[0].text = "Rediger " + title;
      }
      this.gridContextMenuItemsContent = this.gridContextMenuItemsEdit.concat(this.gridContextMenuItemsContent);
    }

    // Concat Header+Content
    this.gridContextMenuItems = this.gridContextMenuItemsHeader.concat(this.gridContextMenuItemsContent);
    this.grid.contextMenuItems = this.gridContextMenuItems;
  }

  public contextMenuItemsIsActive(target: HTMLElement): boolean {
    var hasItemActive: boolean = false;

    if (this.grid.isEdit) {
      return false;
    }

    if (target) {
      for (let i of this.gridContextMenuItems) {
        if (i.separator != true && this.contextMenuItemIsActive(i, target)) {
          hasItemActive = true;
          break;
        }
      }

    }
    return hasItemActive;
  }

  public contextMenuItemIsActive(i: ContextMenuItemModel, target: HTMLElement): boolean {

    if (!i.target || i.target.length <= 1) {
      return true;
    }

    var selector = i.target.substr(1, i.target.length);

    if (selector == 'e-content' && target?.classList?.contains('e-rowcell')) {
      return true;
    }

    if (selector == 'e-headercontent' && target?.classList?.contains('e-headercell')) {
      return true;
    }

    while (target) {
      if (target.classList?.contains('e-grid')) {
        return false;
      }
      if (target.classList?.contains(selector)) {
        return true;
      }
      target = target.parentElement;
    }

    return false;
  }

  /*********************
   * Keyboard shortcuts
   ********************/

  public gridOnKeyDown(e: KeyboardEvent) {
    if (this.isDisabled()) {
      return;
    }
    var stop: boolean = false;
    this.keyboardShortcuts.forEach(shortcut => {
      if (this.keyboardShortcutMatch(e, shortcut) && shortcut.informationOnly !== true) {
        var cancel: boolean = false;
        var rowData = this.appGrid.rowSelectedData();

        if (!rowData && shortcut.cmd.needSelection === true) {
          cancel = true;
        }
        else {
          var target: HTMLElement = <HTMLElement>e?.target;
          var colIndex: string = (target?.tagName == 'TD') ? target?.getAttribute('data-colindex') : '';
          var columnName: string = colIndex?.length > 0 ? this.grid.getColumnByIndex(parseInt(colIndex))?.field : null;
          cancel = this.cmdDispatch(UiCommandSourceEnum.KeyboardShortcut, shortcut.cmd.id, e, columnName, UiActionTypeEnum.KeyboardShortcut, this.grid.currentViewData, rowData, shortcut.cmd);
        }
        if (cancel || shortcut.allowsPreventDefault) {
          stop = true;
        }
      }
    });
    if (stop) {
      e.stopPropagation();
      e.preventDefault();
    }
  }

  public keyboardShortcutsAdd(shortcuts: UiKeyboardShortcut[]) {
    if (shortcuts) {
      shortcuts.forEach(s => this.keyboardShortcuts.unshift(s));
      if (this.grid?.editSettings?.allowEditing &&
        this.grid?.editSettings?.mode == 'Batch') {
        this.keyboardShortcuts.push(this.shortcutBeginEdit);
      }
    }
  }

  public keyboardShortcutEnable(cmdId: string, enable: boolean) {
    //  this.shortcuts.filter(s => s.);
    this.keyboardShortcuts.forEach(shortcut => { });
  }

  public keyboardShortcutMatch(e: KeyboardEvent, s: UiKeyboardShortcut): boolean {
    var match: boolean = (e.code == s.code) && (e.altKey == s.alt) && (e.shiftKey == s.shift) && (e.ctrlKey == s.ctrl) && (s.enabled || s.enabled === undefined) && (!s.informationOnly);
    return match;
  }

  private keyboardShortcutsDefaultModify() {
    if (this.grid) {
      var configs = (<any>this.grid).keyConfigs;
      if (configs) {
        // Remove default - remapped to 'AddRecord'
        var shiftEnter = configs.shiftEnter;
        if (shiftEnter?.length > 0) {
          configs.shiftEnter = '';
          configs.insert = configs.insert?.length > 0 ? configs.insert + ',' + shiftEnter : shiftEnter;
        }

        // Remove defualt - remapped to 'EditRecord'
        /*
        var enter = configs.enter;
        if (enter?.length > 0) {
          configs.enter = '';
          configs.f2 = configs.f2?.length > 0 ? configs.f2 + ',' + enter : enter;
        }
        */
      }
    }
  }

  private formContextService = inject(CerFormContextService);
  private doColumnNavigateFK(event: UiCommandEvent) {
    const target = event.args?.rowInfo?.target;
    const isHeader = target && parentsUntil(target, 'e-headercontent', false) != null;
    const navs = this.formNavigationService.navigationFromUiCommandColumnFK(event, !isHeader);
    if (navs?.length > 0 && navs[0].url?.length > 0) {
      const nav = navs[0];
      if (isHeader) {
        nav.columnNameChild = null;
        nav.columnNameParent = null;
        nav.filterValue = null;
      }
      else {
        this.appGrid.rowSelectEnsure();
      }
      nav.filterSingleRow = !isHeader;
      this.formContextService.navigate(nav);
    }
  }

  public navigationEvent(event: UiCommandEvent) {
    const nav = this.formNavigationService.navigationFromUiCommandEvent(event);
    if (nav) {
      if (nav.columnNameParent?.length) {
        this.appGrid.rowSelectEnsure();
        this.appGrid.design.autoFocus = true;
      }
      this.formContextService.navigate(nav);
    }
  }
}

